/**
 * The theme a business picked, as the values everything else derives from.
 *
 * ThemedForm writes these into the page as CSS custom properties. Custom
 *  questions render in their own document, which inherits nothing, so they
 *  need the same numbers handed to them - hence one place that works them out
 *  rather than two that drift.
 */

export const DEFAULT_FORM_COLOR = '#2C87F3';

export type FormStyles = {
    color?: string;
    font?: string;
    fontSize?: string;
    input?: string;
    button?: string;
    [key: string]: any;
};

export type FormThemeTokens = {
    color: string;
    colorLight: string;
    colorLightest: string;
    fontSize: string;
    fontSizeSm: string;
};

// Source: https://github.com/PimpTrizkit/PJs/wiki/12.-Shade,-Blend-and-Convert-a-Web-Color-(pSBC.js)
// Version 4.1
export const pSBC = (p: any, c0: any, c1?: any, l?: any): string | null => {
    let r,
        g,
        b,
        P,
        f,
        t,
        h,
        m = Math.round,
        a: any = typeof c1 == 'string';
    if (
        typeof p != 'number' ||
        p < -1 ||
        p > 1 ||
        typeof c0 != 'string' ||
        (c0[0] != 'r' && c0[0] != '#') ||
        (c1 && !a)
    )
        return null;
    (h = c0.length > 9),
        (h = a ? (c1.length > 9 ? true : c1 == 'c' ? !h : false) : h),
        (f = (pSBC as any).pSBCr(c0)),
        (P = p < 0),
        (t =
            c1 && c1 != 'c'
                ? (pSBC as any).pSBCr(c1)
                : P
                  ? { r: 0, g: 0, b: 0, a: -1 }
                  : { r: 255, g: 255, b: 255, a: -1 }),
        (p = P ? p * -1 : p),
        (P = 1 - p);
    if (!f || !t) return null;
    if (l)
        (r = m(P * f.r + p * t.r)),
            (g = m(P * f.g + p * t.g)),
            (b = m(P * f.b + p * t.b));
    else
        (r = m((P * f.r ** 2 + p * t.r ** 2) ** 0.5)),
            (g = m((P * f.g ** 2 + p * t.g ** 2) ** 0.5)),
            (b = m((P * f.b ** 2 + p * t.b ** 2) ** 0.5));
    (a = f.a),
        (t = t.a),
        (f = a >= 0 || t >= 0),
        (a = f ? (a < 0 ? t : t < 0 ? a : a * P + t * p) : 0);
    if (h)
        return (
            'rgb' +
            (f ? 'a(' : '(') +
            r +
            ',' +
            g +
            ',' +
            b +
            (f ? ',' + m(a * 1000) / 1000 : '') +
            ')'
        );
    else
        return (
            '#' +
            (
                4294967296 +
                r * 16777216 +
                g * 65536 +
                b * 256 +
                (f ? m(a * 255) : 0)
            )
                .toString(16)
                .slice(1, f ? undefined : -2)
        );
};

(pSBC as any).pSBCr = (d: string) => {
    const i = parseInt;
    let n = d.length,
        x: any = {};
    if (n > 9) {
        const parts: any = d.split(',');
        const [r, g, b, a] = parts;
        n = parts.length;
        if (n < 3 || n > 4) return null;
        (x.r = i(r[3] == 'a' ? r.slice(5) : r.slice(4))),
            (x.g = i(g)),
            (x.b = i(b)),
            (x.a = a ? parseFloat(a) : -1);
    } else {
        if (n == 8 || n == 6 || n < 4) return null;
        if (n < 6)
            d =
                '#' +
                d[1] +
                d[1] +
                d[2] +
                d[2] +
                d[3] +
                d[3] +
                (n > 4 ? d[4] + d[4] : '');
        const v = i(d.slice(1), 16);
        if (n == 9 || n == 5)
            (x.r = (v >> 24) & 255),
                (x.g = (v >> 16) & 255),
                (x.b = (v >> 8) & 255),
                (x.a = Math.round((v & 255) / 0.255) / 1000);
        else
            (x.r = v >> 16),
                (x.g = (v >> 8) & 255),
                (x.b = v & 255),
                (x.a = -1);
    }
    return x;
};

function pick<T>(value: string | undefined, options: Record<string, T>): T {
    if (value && options[value] !== undefined) {
        return options[value];
    }

    return options[Object.keys(options)[0]];
}

export function formThemeTokens(styles?: FormStyles | null): FormThemeTokens {
    const color = styles?.color || DEFAULT_FORM_COLOR;

    return {
        color,
        colorLight: pSBC(0.2, color) || color,
        colorLightest: pSBC(0.7, color) || color,
        fontSize: pick(styles?.fontSize, {
            normal: '16px',
            small: '14px',
            large: '18px',
            'extra-large': '20px',
        }),
        fontSizeSm: pick(styles?.fontSize, {
            normal: '12px',
            small: '10px',
            large: '14px',
            'extra-large': '16px',
        }),
    };
}

/**
 * The same tokens as the CSS custom properties they are set as, which is how
 *  both the form and a custom question's own document receive them.
 */
export function formThemeProperties(styles?: FormStyles | null): string {
    const theme = formThemeTokens(styles);

    return [
        `--breely-color: ${theme.color};`,
        `--breely-color-light: ${theme.colorLight};`,
        `--breely-color-lightest: ${theme.colorLightest};`,
        `--breely-font-size: ${theme.fontSize};`,
        `--breely-font-size-sm: ${theme.fontSizeSm};`,
    ].join('\n    ');
}

export function formFontFamily(styles?: FormStyles | null): string {
    return pick(styles?.font, {
        sans: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        serif: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
        mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    });
}
